import React, { useId } from 'react';
import './StatChart.css';

// ── A SMALL TIME-SERIES CHART ────────────────────────────────────────────────
// Bars for counts, an area+line for continuous series. Plain SVG on purpose:
// this app carries no charting library, and pulling one in for the single
// admin page would add several hundred KB to a bundle CRA already warns about
// on every build. What a dashboard needs — a shape, a scale, and the number
// under the pointer — is about eighty lines.
//
// RESPONSIVE VIA viewBox, not measurement. The SVG is drawn in a fixed
// coordinate space and scaled by CSS, so there is no resize listener, no
// layout read, and nothing to go stale when the sidebar opens.

const W = 720;      // viewBox units, not pixels
const H = 180;
const PAD = { top: 12, right: 8, bottom: 22, left: 40 };

/** Nice round upper bound, so the axis reads 0/25/50 rather than 0/17/34. */
function niceMax(v) {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  const n = v / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
}

const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n));

export default function StatChart({
  data = [],            // [{ date, value }]
  kind = 'bar',         // 'bar' | 'line'
  color = 'var(--admin-accent)',
  valueLabel = '',
  format = fmt,
  // Passed in rather than looked up here: this component draws, and knows
  // nothing about which page or language it is drawing for.
  emptyLabel = '',
}) {
  const gid = useId().replace(/:/g, '');
  if (!data.length) return <p className="sc-empty">{emptyLabel}</p>;

  const max = niceMax(Math.max(...data.map(d => d.value), 0));
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i) => PAD.left + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW);
  const y = (v) => PAD.top + plotH - (v / max) * plotH;

  // Enough labels to orient, few enough to read: first, last and a middle one.
  const tickAt = new Set([0, Math.floor((data.length - 1) / 2), data.length - 1]);
  const dayLabel = (d) => d.slice(5);           // "08-23"

  return (
    <svg className="sc" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={`${valueLabel} over time`} preserveAspectRatio="none">
      {/* Gridlines and the value axis. Three lines is enough to read a height
          against without turning the panel into graph paper. */}
      {[0, 0.5, 1].map(f => (
        <g key={f}>
          <line className="sc-grid" x1={PAD.left} x2={W - PAD.right}
            y1={y(max * f)} y2={y(max * f)} />
          <text className="sc-axis" x={PAD.left - 6} y={y(max * f) + 3} textAnchor="end">
            {format(Math.round(max * f))}
          </text>
        </g>
      ))}

      {kind === 'line' ? (
        <>
          <defs>
            <linearGradient id={`g${gid}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path className="sc-area" fill={`url(#g${gid})`}
            d={`M ${x(0)} ${y(0)} ${data.map((d, i) => `L ${x(i)} ${y(d.value)}`).join(' ')} L ${x(data.length - 1)} ${y(0)} Z`} />
          <path className="sc-line" stroke={color} fill="none"
            d={data.map((d, i) => `${i ? 'L' : 'M'} ${x(i)} ${y(d.value)}`).join(' ')} />
        </>
      ) : (
        data.map((d, i) => {
          // Gaps stay proportional however many bars there are, and a zero
          // still draws a 1-unit sliver so "no events" is visibly a value
          // rather than a hole in the chart.
          const bw = Math.max(1, (plotW / data.length) * 0.62);
          const h = Math.max(d.value > 0 ? 1.5 : 0, (d.value / max) * plotH);
          return (
            <rect key={d.date} className="sc-bar" fill={color}
              x={x(i) - bw / 2} y={PAD.top + plotH - h} width={bw} height={h} rx="1.5">
              {/* The browser's own tooltip — no state, no listeners, and it
                  works with a keyboard and a screen reader. */}
              <title>{`${d.date}: ${format(d.value)} ${valueLabel}`.trim()}</title>
            </rect>
          );
        })
      )}

      {data.map((d, i) => (tickAt.has(i) ? (
        <text key={d.date} className="sc-axis" x={x(i)} y={H - 6} textAnchor="middle">
          {dayLabel(d.date)}
        </text>
      ) : null))}
    </svg>
  );
}
